import { useEffect, useMemo, useState } from 'react'
import { useQueryClient, type QueryFilters } from '@tanstack/react-query'
import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source'
import { resolveAuthToken } from '../lib/api'
import { invalidationsFor, liveBackoffDelay, mergeInvalidations } from '../lib/liveEvents'
import type { LiveStatus } from '../contexts/LiveStatusContext'

/**
 * Live order events (ADR-W12) for the backoffice.
 *
 * Holds GET /api/live/stream?scope=backoffice open while the tab is visible and
 * turns its frames into React Query invalidations (lib/liveEvents). Shell mounts
 * it once and provides the result as LiveStatusContext, so the order polls can
 * stop while the stream is open and come back the moment it is not.
 *
 * Everything works without the server: a missing or failing stream leaves
 * `connected` false, so every screen keeps polling exactly as before.
 */

const EVENT_STREAM = 'text/event-stream'

/** Frames are applied together, this long after the first frame of a batch. */
export const LIVE_BATCH_MS = 250
/** A connection that stayed open longer than this resets the backoff. */
export const LIVE_STABLE_MS = 60_000
/** 404 means the feature is off on the server: look again this often. */
export const LIVE_FEATURE_OFF_RETRY_MS = 5 * 60_000
/**
 * The server sends a `: ping` every 25 s. A stream that has said nothing for
 * this long is dead even though its socket never closed (a node gone behind the
 * ingress, a NAT that forgot the connection after a network change): drop it
 * and reconnect, rather than sit "connected" with the polls off and no events.
 */
export const LIVE_SILENCE_MS = 60_000
/**
 * A clean server close is answered at once, but spread over this much: a pod
 * that shuts down closes all of its streams in the same instant, and each
 * reconnect's `ready` reloads every order query on screen.
 */
export const LIVE_CLOSE_JITTER_MS = 1_000

export function liveStreamUrl(): string {
  const base = String(import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '')
  return `${base}/live/stream?scope=backoffice`
}

/** onopen got something other than 200 with text/event-stream. */
class LiveStreamOpenError extends Error {
  readonly status: number

  constructor(status: number, contentType: string | null) {
    super(`Live stream not opened: HTTP ${status}, content-type ${contentType ?? 'none'}`)
    this.name = 'LiveStreamOpenError'
    this.status = status
  }
}

/** The server ended the stream: its 15-minute close, or a pod going away. */
class LiveStreamClosed extends Error {
  constructor() {
    super('Live stream closed by the server')
    this.name = 'LiveStreamClosed'
  }
}

/** Nothing arrived for LIVE_SILENCE_MS, not even a ping: dropped by the client. */
class LiveStreamSilent extends Error {
  constructor() {
    super(`Live stream silent for ${LIVE_SILENCE_MS / 1000} s`)
    this.name = 'LiveStreamSilent'
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0 || signal.aborted) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal.addEventListener('abort', done)
  })
}

/** Resolves once the tab is visible (at once if it is), or when `signal` aborts. */
function waitUntilVisible(signal: AbortSignal): Promise<void> {
  if (!document.hidden || signal.aborted) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      document.removeEventListener('visibilitychange', onChange)
      signal.removeEventListener('abort', done)
      resolve()
    }
    const onChange = () => {
      if (!document.hidden) done()
    }
    document.addEventListener('visibilitychange', onChange)
    signal.addEventListener('abort', done)
  })
}

interface LiveStreamOptions {
  signal: AbortSignal
  onStatus: (connected: boolean) => void
  onFrame: (message: EventSourceMessage) => void
}

/**
 * The connect / reconnect loop. Each pass is one connection with a fresh token.
 *
 * fetchEventSource keeps the headers it was given for the life of the call and,
 * left to itself, retries with them. Clerk tokens live about a minute, so those
 * retries would carry an expired token. Here onclose and onerror always throw,
 * which makes the library give up and reject; this loop then waits and opens a
 * new connection with a new token:
 *
 * - closed by the server after a connection that lasted over a minute: at once
 *   (within LIVE_CLOSE_JITTER_MS);
 * - 401: one immediate retry, then the backoff;
 * - 404 (feature off): every 5 minutes;
 * - anything else, including a 200 that is not text/event-stream, a close
 *   within the first minute, or a stream silent for LIVE_SILENCE_MS: 1, 2, 5,
 *   10, 30, then 60 s, ±20 %, reset by a connection that stayed open over a
 *   minute.
 *
 * `connected` turns true on the first frame of a connection (`ready` comes
 * first), not on the response headers: a proxy that holds the body back would
 * otherwise switch the polls off while nothing arrives.
 *
 * openWhenHidden is false: the library aborts the request when the tab hides and
 * reopens it, with the same headers, when the tab shows again. That reopen gets
 * `ready` first, which refreshes what is on screen. If its token has expired by
 * then, the 401 lands in onerror, the call rejects, and this loop takes over with
 * a fresh token, so there is never more than one connection or one loop. The
 * silence timer is off while the tab is hidden.
 */
async function runLiveStream({ signal, onStatus, onFrame }: LiveStreamOptions): Promise<void> {
  const url = liveStreamUrl()
  let attempt = 0
  let retried401 = false

  // The library drops the request while the tab is hidden; say so, so the polls
  // (which React Query pauses in a hidden tab anyway) are armed when it shows.
  const onVisibility = () => {
    if (document.hidden) onStatus(false)
  }
  document.addEventListener('visibilitychange', onVisibility)

  try {
    while (!signal.aborted) {
      await waitUntilVisible(signal)
      if (signal.aborted) return
      const token = await resolveAuthToken()
      if (signal.aborted) return
      // Hidden again while the token was on its way: wait for the tab first.
      if (document.hidden) continue

      const connection = { openedAt: null as number | null, spoken: false }
      let failure: unknown

      if (!token) {
        failure = new Error('No auth token for the live stream')
      } else {
        // One controller per connection: the library adds an abort listener to
        // the signal it gets and never removes it, so the hook's own signal would
        // collect one per reconnect.
        const controller = new AbortController()
        const abort = () => controller.abort()
        signal.addEventListener('abort', abort)

        // Every frame, the ping included, re-arms the silence timer. Off while
        // the tab is hidden: the library has dropped the request then, and
        // reopens it when the tab shows.
        let silent = false
        let silenceTimer: ReturnType<typeof setTimeout> | undefined
        const listen = () => {
          clearTimeout(silenceTimer)
          silenceTimer = setTimeout(() => {
            silent = true
            controller.abort()
          }, LIVE_SILENCE_MS)
        }
        const onConnectionVisibility = () => {
          if (document.hidden) clearTimeout(silenceTimer)
          else listen()
        }
        document.addEventListener('visibilitychange', onConnectionVisibility)
        listen()

        try {
          await fetchEventSource(url, {
            headers: { Authorization: `Bearer ${token}`, accept: EVENT_STREAM },
            openWhenHidden: false,
            signal: controller.signal,
            async onopen(response) {
              const contentType = response.headers.get('content-type')
              if (response.status !== 200 || !contentType?.startsWith(EVENT_STREAM)) {
                throw new LiveStreamOpenError(response.status, contentType)
              }
              connection.openedAt = Date.now()
              connection.spoken = false
              retried401 = false
              listen()
            },
            onmessage(message) {
              listen()
              if (!connection.spoken) {
                connection.spoken = true
                onStatus(true)
              }
              onFrame(message)
            },
            onclose() {
              throw new LiveStreamClosed()
            },
            onerror(err) {
              throw err
            },
          })
          // With onclose throwing, the call only resolves when it was aborted:
          // by the hook, or by the silence timer.
          if (signal.aborted) return
          failure = silent ? new LiveStreamSilent() : new LiveStreamClosed()
        } catch (err) {
          failure = err
        } finally {
          clearTimeout(silenceTimer)
          document.removeEventListener('visibilitychange', onConnectionVisibility)
          signal.removeEventListener('abort', abort)
        }
      }

      onStatus(false)
      if (signal.aborted) return

      const stable =
        connection.openedAt !== null && Date.now() - connection.openedAt > LIVE_STABLE_MS
      if (stable) attempt = 0
      const status = failure instanceof LiveStreamOpenError ? failure.status : null

      let delay: number
      if (failure instanceof LiveStreamClosed && stable) {
        delay = Math.round(Math.random() * LIVE_CLOSE_JITTER_MS)
      } else if (status === 404) {
        delay = LIVE_FEATURE_OFF_RETRY_MS
      } else if (status === 401 && !retried401) {
        retried401 = true
        delay = 0
      } else {
        delay = liveBackoffDelay(attempt)
        attempt += 1
      }
      await sleep(delay, signal)
    }
  } finally {
    document.removeEventListener('visibilitychange', onVisibility)
  }
}

/**
 * Opens the live order stream for as long as the calling component is mounted,
 * and returns whether it is open. Mount it once (Shell does). Company switch and
 * impersonation reload the page, so unmount is the only teardown it needs.
 */
export function useLiveEvents(): LiveStatus {
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let pending: QueryFilters[] = []
    let timer: ReturnType<typeof setTimeout> | undefined

    // invalidateQueries cancels a refetch that is still running and starts
    // another. In a burst of events that would cancel every query slower than
    // the batch window before it lands. So a query that is still loading waits
    // for a later window and is refreshed once it is done, which still picks up
    // the last event of the burst. Only that query waits: the others under the
    // same prefix (the board's other columns behind a slow "Load more") are
    // refreshed at once.
    const flush = () => {
      timer = undefined
      const batch = mergeInvalidations(pending)
      pending = []
      for (const filters of batch) {
        const busy = new Set(
          queryClient.getQueryCache().findAll({ ...filters, fetchStatus: 'fetching' }),
        )
        if (busy.size === 0) {
          void queryClient.invalidateQueries(filters)
          continue
        }
        void queryClient.invalidateQueries({ ...filters, predicate: (query) => !busy.has(query) })
        for (const query of busy) pending.push({ queryKey: query.queryKey })
      }
      if (pending.length > 0) timer = setTimeout(flush, LIVE_BATCH_MS)
    }

    const onFrame = (message: EventSourceMessage) => {
      const filters = invalidationsFor(message.event, message.data)
      if (filters.length === 0) return
      pending.push(...filters)
      if (timer === undefined) timer = setTimeout(flush, LIVE_BATCH_MS)
    }

    runLiveStream({
      signal: controller.signal,
      onStatus: (next) => {
        if (!controller.signal.aborted) setConnected(next)
      },
      onFrame,
    }).catch((err) => {
      // Not expected: the loop handles its own failures. Polling carries on.
      console.error('Live order stream stopped', err)
      if (!controller.signal.aborted) setConnected(false)
    })

    return () => {
      controller.abort()
      if (timer !== undefined) clearTimeout(timer)
      pending = []
      setConnected(false)
    }
  }, [queryClient])

  return useMemo(() => ({ connected }), [connected])
}
